import { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// store
import { setBreadcrumbs } from '@/store/slices/breadcrumb';

// services
import { accountingCenterService } from '@/services/accounting-centers.service';

// utils
import { calculateChartData, getChartConfigs } from '@/utils/chartCalculations';

// components
import { Accordion } from '@/components/ui/accordion';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PieGraph from '@/components/organisms/graphs/PieGraph';
import { Button } from '@/components/ui/button';
import ACItemAccordion from '@/components/molecules/ACItemAccordion';

// icons
import editIcon from '@/assets/icons/edit.svg';
import percentageIcon from '@/assets/icons/percentage.svg';
import euroIcon from '@/assets/icons/euro.svg';
import infoIcon from '@/assets/icons/info.svg';
import invoiceIcon from '@/assets/icons/invoice.svg';
import noInvoiceIcon from '@/assets/icons/no-invoice.svg';
import departmentACIcon from '@/assets/icons/department-acs.svg';
import productsACIcon from '@/assets/icons/product-and-services.svg';
import projectsACIcon from '@/assets/icons/project-acs.svg';
import contractsACIcon from '@/assets/icons/contracts-acs.svg';

import acExpanatoryImg from '@/assets/images/ac-explanatory-1.png';
import acExpanatoryImg2 from '@/assets/images/ac-explanatory-2.png';
import acExpanatoryImg3 from '@/assets/images/ac-explanatory-3.png';

import { PAGE } from '@/routes';
import ExplanatoryModal from '@/components/molecules/ExplanatoryModal';

type ACType = 'DEPARTMENT_ACCOUNTING' | 'PRODUCTS_AND_SERVICES' | 'PROJECTS' | 'CONTRACTS';

const ACCOUNTING_CENTERS_ICONS: Record<ACType, string> = {
  DEPARTMENT_ACCOUNTING: departmentACIcon,
  PRODUCTS_AND_SERVICES: productsACIcon,
  PROJECTS: projectsACIcon,
  CONTRACTS: contractsACIcon
};

const steps = [
  {
    title: 'Understanding MAEs',
    message:
      'MAEs are customizable entities designed for better cashflow management. They are categorized into four types: Departments, Products & Services, Projects, and Contract Entities.',
    img: acExpanatoryImg
  },
  {
    title: 'Setting Limits with ACMEV and ACRT',
    message:
      'ACMEV is the maximum spending limit for each AC. ACRT is the revenue target assigned to each AC. Both values are aligned with the overall BMEV and BTRT.',
    img: acExpanatoryImg2
  },
  {
    title: 'Linking Invoices to MAEs',
    message:
      'Invoices can be allocated to specific MAEs, providing detailed insights into the financial activities of each category.',
    img: acExpanatoryImg3
  }
];

const AccountingCentersPage = () => {
  const [graphViewMode, setGraphViewMode] = useState('percentage');
  const [accountCenters, setAccountingCenters] = useState<any[]>([]);
  const [openItems, setOpenItems] = useState<string[]>([]);

  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const { budgetId, acType } = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const location = useLocation();
  const acCenter = location.state?.acCenter as any | undefined;

  const fetchAccountCenterDetails = async () => {
    // Fetch accounting center details
    try {
      const { data: accountingCentersData } = await accountingCenterService.getAccountingCentersByType(
        budgetId || '',
        String(acType).toUpperCase() || ''
      );

      setAccountingCenters(accountingCentersData);
    } catch (error) {
      console.error('Error fetching accounting center details:', error);
    }
  };

  useEffect(() => {
    fetchAccountCenterDetails();
  }, [acType]);

  useEffect(() => {
    // Set breadcrumbs when component mounts
    dispatch(
      setBreadcrumbs([
        {
          title: 'Budget',
          path: `/budgets/${budgetId}`
        },
        {
          title: acCenter?.name ? acCenter?.name + ' Overview' : ' Accounting Center Overview',
          path: ``
        }
      ])
    );
  }, []);

  const handleEditClick = () => {
    navigate(PAGE.ACCOUNTING_CENTER.children.ADJUST, { state: { acCenter, accountCenters } });
  };

  const handleBankAccountClick = () => {};

  const { acmevChartData, acrtChartData } = calculateChartData(acCenter);
  const { acmevChartConfig, acrtChartConfig } = getChartConfigs('secondary');

  const handleAddClick = () => {
    navigate(PAGE.ACCOUNTING_CENTER.children.ADD);
  };

  return (
    <div className="h-full flex items-stretch justify-between gap-4">
      {/* Main - ACMEV & ACRT */}
      <div className="flex-1 flex flex-col gap-4">
        <div className="border bg-white rounded-[16px] p-4">
          <header className="flex items-start justify-between gap-4">
            <h2 className="text-2xl font-semibold uppercase">{acType?.split('_')[0]} ACCOUNTING CENTERS</h2>
            <div className="flex flex-col items-end gap-3">
              <button
                onClick={() => {
                  setCurrentStep(0);
                  setIsOpen(true);
                }}
              >
                <img className="w-[28px] h-[28px]" src={infoIcon} alt="vault icon" />
              </button>

              <Tabs defaultValue="percentage" value={graphViewMode} onValueChange={setGraphViewMode}>
                <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem]">
                  <TabsTrigger value="percentage" className="group md:py-[0.3rem]">
                    <img
                      src={percentageIcon}
                      alt="percentage-icon"
                      width={13}
                      height={17}
                      className="group-data-[state=active]:brightness-[100]"
                    />
                  </TabsTrigger>
                  <TabsTrigger value="euro" className="group md:py-[0.3rem]">
                    <img
                      src={euroIcon}
                      alt="euro-icon"
                      width={12}
                      height={12}
                      className="w-[12px] h-[12px] group-data-[state=active]:brightness-[100]"
                    />
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </header>

          <div className="flex items-start justify-between gap-3 mt-3">
            <PieGraph
              title="Remaining ACMEV"
              titleValue={(() => {
                if (graphViewMode === 'percentage' && acmevChartData) {
                  return acmevChartData.length > 0 ? `${acmevChartData[0].value}%` : '0%';
                } else {
                  // Euro mode - calculate actual value
                  const acmevTotal = acCenter?.acmev || 0;
                  const acmevUsed = acCenter?.acmev_used || 0;
                  const acmevRemaining = Math.max(0, acmevTotal - acmevUsed);
                  return `€${acmevRemaining.toLocaleString()}`;
                }
              })()}
              chartConfig={acmevChartConfig}
              chartData={acmevChartData || []}
              className="bg-white rounded-[16px] p-[0.85rem] flex-1 md:border border-lightGray md:py-4 md:flex md:items-center md:justify-center md:flex-col"
              outerRadius={64}
              minHeight={140}
            />

            <PieGraph
              title="Achieved ACRT"
              titleValue={(() => {
                if (graphViewMode === 'percentage') {
                  const achievedItem = acrtChartData && acrtChartData.find((item) => item.key === 'achieved');
                  return achievedItem ? `${achievedItem.value}%` : '0%';
                } else {
                  // Euro mode - display the actual achieved value
                  const acrtAchieved = acCenter?.acrt_achieved || 0;
                  return `€${acrtAchieved.toLocaleString()}`;
                }
              })()}
              chartConfig={acrtChartConfig}
              chartData={acrtChartData || []}
              variant="primary"
              className="bg-white rounded-[16px] p-[0.85rem] flex-1 md:border border-lightGray md:py-4 md:flex md:items-center md:justify-center md:flex-col"
              outerRadius={64}
              minHeight={140}
            />
          </div>
        </div>
        <div className="flex-1 flex flex-col border bg-white rounded-[16px] p-4">
          <header>
            <h4 className="flex items-center gap-2">
              <img src={invoiceIcon} alt="invoice-icon" />
              <span className="font-bold">Recent Invoice</span>
            </h4>
          </header>
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <div className="flex items-center gap-2">
              <img className="w-[40px] h-[40px]" src={noInvoiceIcon} alt="no-invoice-icon" />
              <div className="flex flex-col items-start justify-center">
                <p className="font-medium leading-[140%]">There are no Invoice to show</p>
                <span className="text-secondary text-sm leading-[125%]">
                  Please create a new invoice to allocate budget!
                </span>
              </div>
            </div>
            <Button variant={'primary-reverse'} className="rounded-full px-6">
              Create an Invoice
            </Button>
          </div>
        </div>
      </div>

      {/* Sidebar - ALL DEPARTMENT AC */}
      <div className="flex-1 min-w-[360px] max-w-[400px] border bg-white rounded-[16px] p-4 font-bold text-base overflow-y-auto">
        <header className="flex items-center justify-between gap-5">
          <span>All Department AC</span>

          <button onClick={handleEditClick} title="Edit">
            <img className="w-[28px] h-[28px]" src={editIcon} alt="vault icon" />
          </button>
        </header>

        <Accordion type="multiple" value={openItems} onValueChange={setOpenItems} className="flex flex-col gap-4 mt-5">
          {accountCenters && accountCenters?.length > 0 ? (
            accountCenters.map((ac: any) => (
              <ACItemAccordion
                key={ac._id}
                ac={ac}
                acCenter={acCenter}
                graphViewMode={graphViewMode}
                open={openItems.includes(ac._id)}
                onToggle={() => {
                  setOpenItems((prev) =>
                    prev.includes(ac._id) ? prev.filter((id) => id !== ac._id) : [...prev, ac._id]
                  );
                }}
              />
            ))
          ) : (
            <p className="text-sm text-secondary text-center py-4 font-normal">No accounting center found</p>
          )}
        </Accordion>
      </div>

      {/* Add Button */}

      <div className="rounded-[22px] flex-1 flex items-center justify-center absolute bottom-[26px] right-[36px] shadow-[0px_4px_4px_0px_#00000040]">
        <Button onClick={handleAddClick} className="rounded-full px-6 w-[64px] h-[64px] rounded-[22px]">
          <span className="text-white text-[2.5rem] font-light">+</span>
        </Button>
      </div>

      {/* Explanatory Modal */}
      {isOpen && (
        <ExplanatoryModal
          isOpen={isOpen}
          steps={steps}
          currentStep={currentStep}
          buttonText="Next"
          onButtonClick={() => {
            setCurrentStep(currentStep + 1);
          }}
          onClose={() => {
            setIsOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default AccountingCentersPage;
